import React, { useState, useContext } from 'react';
import { Upload, X, AlertCircle } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { createProgram } from '../services/programService';

const ProgramUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const { currentUser, userRole } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
  });
  const [flyerImage, setFlyerImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  // Only allow admin roles
  const isAdmin = ['master_admin', 'overseer', 'colony_prime_minister'].includes(userRole);
  
  if (!isAdmin) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md">
          <div className="flex items-center gap-3 text-red-600 mb-4">
            <AlertCircle size={24} />
            <span>Only admins can upload programs</span>
          </div>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB');
        return;
      }
      setFlyerImage(file);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.title.trim() || !formData.description.trim()) {
        setError('Title and description are required');
        setLoading(false);
        return;
      }

      if (!flyerImage) {
        setError('Flyer image is required');
        setLoading(false);
        return;
      }

      const programData = {
        title: formData.title,
        description: formData.description,
        createdBy: currentUser.uid,
      };

      await createProgram(programData, flyerImage);
      onSuccess();
      setFormData({ title: '', description: '' });
      setFlyerImage(null);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to upload program');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Upload New Program</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Program Title */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Program Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter program title"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading}
            />
          </div>

          {/* Program Description */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Program Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter detailed program description"
              rows="5"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              disabled={loading}
            />
          </div>

          {/* Flyer Image Upload */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Flyer Image *
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                id="flyer-input"
                disabled={loading}
              />
              <label htmlFor="flyer-input" className="cursor-pointer">
                <Upload className="mx-auto mb-2 text-gray-400" size={32} />
                <p className="text-sm text-gray-600">
                  {flyerImage ? flyerImage.name : 'Click or drag image here'}
                </p>
                <p className="text-xs text-gray-400 mt-1">Max 5MB</p>
              </label>
            </div>
          </div>

          {/* Preview */}
          {flyerImage && (
            <div className="mb-6">
              <p className="text-sm font-semibold text-gray-700 mb-2">Preview:</p>
              <img
                src={URL.createObjectURL(flyerImage)}
                alt="Preview"
                className="h-40 object-cover rounded-lg"
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Uploading...' : 'Upload Program'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProgramUploadModal;
